/**
 * MyClips V3 — L-6 Light-Only Reset
 *
 * L-6 değişikliği: UI her zaman LIGHT. Telefon OS'u dark mode'da
 * olsa bile MyClips beyaz render olur. Dark tema kod yolları
 * bundle'da kalır (ileride premium opt-in için), UI'dan erişilemez.
 *
 * Kullanıcı tercihi localStorage'a yazılır (`mlv3.theme`).
 * Hook, `.mlv3-root` element(ler)inin `data-theme` attribute'unu
 * günceller — CSS globals.css buna göre tokenleri seçer.
 */

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "mlv3.theme";

/** Kullanıcının seçebildiği tercih (`VALID` listesinin birebir karşılığı). */
export type ThemePreference = "auto" | "light" | "dark";
/** DOM'a/`data-mlv3-theme-effective`'e yazılan çözülmüş değer. */
export type EffectiveTheme = "light" | "dark";

const VALID: readonly string[] = ["auto", "light", "dark"];

/** `VALID.includes(...)` kontrolünün tip düzeyindeki karşılığı — davranış aynı. */
function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === "string" && VALID.includes(value);
}

function readStored(): ThemePreference {
  try {
    const v = window.localStorage?.getItem(STORAGE_KEY);
    if (isThemePreference(v)) return v;
  } catch (_) {}
  // L-6: default "light" — OS dark mode'u takip etme
  return "light";
}

function writeStored(value: ThemePreference): void {
  try {
    if (value === "auto") {
      window.localStorage?.removeItem(STORAGE_KEY);
    } else {
      window.localStorage?.setItem(STORAGE_KEY, value);
    }
  } catch (_) {}
}

function resolveEffective(preference: ThemePreference): EffectiveTheme {
  if (preference === "light" || preference === "dark") return preference;
  // L-6: auto artık OS dark'ı takip etmez. Light default.
  return "light";
}

function applyToDom(preference: ThemePreference): void {
  if (typeof document === "undefined") return;
  const roots = document.querySelectorAll(".mlv3-root");
  roots.forEach((el) => {
    if (preference === "auto") {
      el.removeAttribute("data-theme");
    } else {
      el.setAttribute("data-theme", preference);
    }
  });
  document.body?.setAttribute(
    "data-mlv3-theme-effective",
    resolveEffective(preference),
  );
}

/**
 * Render öncesi erken çağrılır — flash-of-wrong-theme önlemi.
 */
export function initThemeEarly() {
  if (typeof document === "undefined") return;
  const pref = readStored();
  // L-6: stored "dark" bile olsa light'a zorla
  applyToDom(pref === "dark" ? "light" : pref);
}

/**
 * React hook: tema tercihini oku + set et.
 */
export function useThemePreference() {
  const [preference, setPreference] = useState(() => readStored());
  const [effective, setEffective] = useState(() => resolveEffective(preference));

  useEffect(() => {
    writeStored(preference);
    // L-6: dark stored olsa bile DOM'a light uygula
    applyToDom(preference === "dark" ? "light" : preference);
    setEffective(resolveEffective(preference));
  }, [preference]);

  // L-6: auto modunda OS dark değişikliğini artık takip etme
  // (resolveEffective auto → light döndürüyor)

  // Parametre `string`: gövde zaten çalışma anında doğruluyor (geçersiz değer
  // sessizce yutuluyor) — bu kapıyı tip daraltmasıyla kapatmak davranışı değiştirirdi.
  const setPreferenceSafe = useCallback((next: string) => {
    if (!isThemePreference(next)) return;
    setPreference(next);
  }, []);

  const toggle = useCallback(() => {
    setPreference((p) => {
      if (p === "auto") return "light";
      if (p === "light") return "dark";
      return "auto";
    });
  }, []);

  return { preference, effective, setPreference: setPreferenceSafe, toggle };
}

export default useThemePreference;
